import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { helpAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, X, Send, Sparkles, Loader2, BookOpen } from 'lucide-react';

const SUGGESTED_PROMPTS = [
  "How do I create a new project?",
  "How do CRM stages work?",
  "What's an Assembly?",
  "How do I sync with Google Calendar?",
];

// Routes where the bubble should NOT render
const HIDDEN_ROUTES = ['/login', '/', '/share'];

export default function HelperChatBubble() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Hide on login / landing / magic-link routes
  const isHidden = !user || HIDDEN_ROUTES.some(r => r === '/' ? location.pathname === '/' : location.pathname.startsWith(r));

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: message, created_at: new Date().toISOString() };
    setMessages(m => [...m, userMsg]);
    setLoading(true);
    try {
      const res = await helpAPI.chat({ message, session_id: sessionId });
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(m => [...m, { role: 'assistant', content: res.data.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      setMessages(m => [...m, { role: 'assistant', content: `Sorry, I couldn't reach the helper. ${err.response?.data?.detail || err.message}`, created_at: new Date().toISOString(), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    setMessages([]); setSessionId(null); setInput('');
  };

  if (isHidden) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 h-14 px-5 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2"
          data-testid="helper-chat-open-btn"
          aria-label="Open The Eleventh Helper"
        >
          <MessageCircle className="h-5 w-5" />
          <span className="font-medium text-sm">Ask The Eleventh</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-50 w-[95vw] sm:w-[420px] h-[75vh] sm:h-[600px] max-h-[90vh] bg-background rounded-2xl shadow-2xl border flex flex-col overflow-hidden"
          data-testid="helper-chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">The Eleventh Helper</div>
              <div className="text-xs text-white/75">Powered by AI · Ask anything about the app</div>
            </div>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={resetChat} className="text-white hover:bg-white/10 h-7 px-2 text-xs" data-testid="helper-reset-btn">
                New chat
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)} className="text-white hover:bg-white/10 h-7 w-7" data-testid="helper-close-btn" aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" ref={scrollRef} data-testid="helper-messages">
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100">
                  <div className="text-sm">Hi {user?.name?.split(' ')[0] || 'there'} <span role="img" aria-label="wave">👋</span> I'm your Eleventh Helper. Ask me anything about the software — features, menus, or workflows.</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-2">TRY ASKING</div>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTED_PROMPTS.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => send(p)}
                        className="text-xs px-3 py-1.5 rounded-full border bg-card hover:bg-muted text-left max-w-full"
                        data-testid={`helper-suggest-${i}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`} data-testid={`helper-msg-${i}`}>
                {m.role === 'assistant' && (
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3.5 w-3.5 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : m.error ? 'bg-rose-50 text-rose-700 border border-rose-200 rounded-bl-sm' : 'bg-muted rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center">
                  <Sparkles className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="bg-muted rounded-2xl px-3 py-2 rounded-bl-sm flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span className="text-xs text-muted-foreground">Thinking...</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t bg-card flex items-center gap-2"
          >
            <Input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask about any feature..."
              disabled={loading}
              data-testid="helper-input"
              className="flex-1"
            />
            <Button type="submit" size="icon" disabled={!input.trim() || loading} data-testid="helper-send-btn" aria-label="Send">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
          <div className="px-3 pb-2 text-[10px] text-muted-foreground flex items-center gap-1 justify-center">
            <BookOpen className="h-2.5 w-2.5" /> Answers are AI-generated · verify critical steps
          </div>
        </div>
      )}
    </>
  );
}
